import PropTypes from 'prop-types';
import React, { Fragment } from 'react';
import withStyles from 'isomorphic-style-loader/withStyles'
import { defineMessages, FormattedMessage, FormattedDate, injectIntl } from 'react-intl';

import { colorTeal } from '../../../utils/variables.scss';
import { withRtl } from '../../../components/hoc';
import { formatTime, formatDate, isMS, customisedIntlFormatMessage } from '../../../utils/helpers';
import Default, { Mobile } from '../../../utils/mediaQueries';
import Button from '../../../components/Button';
import ButtonAction from '../../../components/ButtonAction';
import Tooltip from '../../../components/Tooltip';
import ScheduleButton from '../ScheduleButton';
import messages from '../../../locale/messages';
import style from './ScheduleInfo.scss';
import { LA_GUARDIA_DBN } from '../../../constants';

const localMessages = defineMessages({
  scheduleDeadlinePassed: {
    id: 'schedule.ScheduleDeadlinePassed',
    defaultMessage: 'The Schedule Deadline Has Passed',
    description: 'Message shown when the schedule deadline has passed',
  },
  programNoSchedule: {
    id: 'schedule.ProgramNoSchedule',
    defaultMessage: 'Program Cannot Currently be Scheduled',
    description: 'Message shown when a program cannot be scheduled',
  },
  appointmentScheduled: {
    id: 'schedule.ApointmentScheduled',
    defaultMessage: 'Appointment Scheduled',
    description: 'The appointment scheduled string',
  },
  buttonReScheduleCancelWithProgramSchoolDescription: {
    id: 'schedule.buttonReScheduleCancelWithProgramSchoolDescription',
    defaultMessage: 'Reschedule (or cancel) program: {programTitle}, for school: {schoolName}',
    description: 'Reschedule or cancel program message',
  },
  buttonScheduleAppointmentWithProgramSchoolDescription: {
    id: 'schedule.buttonScheduleAppointmentWithProgramSchoolDescription',
    defaultMessage: 'Schedule appointment for program: {programTitle} and school: {schoolName}',
    description: 'Schedule appointment for program message',
  },
  buttonUnExpressInterestWithProgramSchoolDescription: {
    id: 'schedule.buttonUnExpressInterestWithProgramSchoolDescription',
    defaultMessage: 'Un-Express interest for program: {programTitle} and school: {schoolName}',
    description: 'Un-Express interest for program message',
  },
  buttonExpressInterestWithProgramSchoolDescription: {
    id: 'schedule.buttonExpressInterestWithProgramSchoolDescription',
    defaultMessage: 'Express interest for program: {programTitle} and school: {schoolName}',
    description: 'Express interest for program message',
  },
  buttonUnExpressInterestLaGuardiaWithProgramSchoolDescription: {
    id: 'schedule.buttonUnExpressInterestLaGuardiaWithProgramSchoolDescription',
    defaultMessage: 'Cancel audition for program: {programTitle} and school: {schoolName}',
    description: 'Cancel audition for program message',
  },
  buttonExpressInterestLaGuardiaWithProgramSchoolDescription: {
    id: 'schedule.buttonExpressInterestLaGuardiaWithProgramSchoolDescription',
    defaultMessage: 'Register audition for program: {programTitle} and school: {schoolName}',
    description: 'Register audition for program message',
  },
  buttonCancelRegistrationWithProgramSchoolDescription: {
    id: 'schedule.buttonCancelRegistrationWithProgramSchoolDescription',
    defaultMessage: 'Cancel registration for program: {programTitle} and school: {schoolName}',
    description: 'Cancel registration for program message',
  },
});

class ScheduleInfo extends React.PureComponent {
  initScheduling = (event, program) => {
    event.stopPropagation();
    this.props.initScheduling(program);
  };

  removeNoMatchingTimeSlot = (event, program) => {
    event.stopPropagation();
    this.props.removeNoMatchingTimeSlot(program.schedule.no_matching_time_slot.id);
  };

  renderNonEligibleTooltip = (button, mobile = false) => {
    const { program, studentFirstName, eligible, intl } = this.props;

    if (program.schedule && program.schedule.deadline_passed) {
      return null;
    }

    if (eligible) {
      return button;
    }

    let mobileProps = {};
    if (mobile) {
      mobileProps = {
        top: '40px',
        left: '110px',
      };
    }

    return (
      <div className={style.tip}>
        <Tooltip
          icon={button}
          ariaLabel={customisedIntlFormatMessage(intl, messages.schoolProgramNotEligibleInfo, {
            programTitle: program.name,
            schoolName: program.school.name,
          })}
          {...mobileProps}
        >
          <p>
            <FormattedMessage
              {...messages.schoolProgramNotEligibleFirstParagraph}
              values={{ firstName: studentFirstName }}
            />
          </p>
          <p className={style.italic}>
            <FormattedMessage {...messages.schoolProgramNotEligibleSecondParagraph} />
          </p>
        </Tooltip>
      </div>
    );
  };

  renderScheduleDeadline = () => {
    // Returns a deadline passed message if there's a deadline and it's passed
    // otherwise if there's a deadline returns that unformatted failing that it just returns a dash
    // Previously used the FormattedDate component which returned incorrectly if there wasn't a time along with the date.
    const { program } = this.props;
    if (program.schedule) {
      if (program.schedule.deadline_passed) {
        return <FormattedMessage {...localMessages.scheduleDeadlinePassed} />;
      }
      return program.schedule.deadline;
    }
    return '-';
  };

  renderForDetailView = labels => {
    const { program, eligible } = this.props;

    const isExpressionOfInterest = program.schedule && program.schedule.express_interest;
    const hasNoMatchingTimeSlot = program.schedule && program.schedule.no_matching_time_slot;

    if (!labels.label) {
      return null;
    }

    return (
      <div className={style.scheduleInfoContainer}>
        <div className={style.scheduleInfo}>
          {!isExpressionOfInterest && program.appointment ? (
            <div className={style.scheduleInfoAdmissionMethodContainer}>
              <div>
                <div className={style.scheduleInfoTitle}>
                  <FormattedMessage {...localMessages.appointmentScheduled} />
                </div>
                <Fragment>
                  <FormattedDate value={program.appointment.time_slot.start_at} />
                  &nbsp;
                  {formatTime(program.appointment.time_slot.start_at)}
                </Fragment>
              </div>
            </div>
          ) : (
            ''
          )}
          <div
            className={
              !isExpressionOfInterest && program.appointment
                ? style.scheduleInfoDeadlineContainer
                : style.scheduleInfoDeadlineContainerStandalone
            }
          >
            <div className={style.scheduleInfoTitle}>
              <FormattedMessage {...messages.generalStringScheduleDeadline} />
            </div>
            <div>{this.renderScheduleDeadline()}</div>
          </div>
        </div>
        <Default>
          <div className={style.scheduleInfoScheduleButtonContainerDetails}>
            {this.renderNonEligibleTooltip(
              <Button
                label={labels.label}
                ariaLabel={labels.ariaLabel}
                onClick={
                  hasNoMatchingTimeSlot
                    ? event => this.removeNoMatchingTimeSlot(event, program)
                    : event => this.initScheduling(event, program)
                }
                iconLeft="Calendar-Outline"
                iconColor={colorTeal}
                disabled={!eligible}
              />,
            )}
          </div>
        </Default>
        <Mobile>
          <div className={style.scheduleInfoScheduleButtonContainerDetailsMobile}>
            {this.renderNonEligibleTooltip(
              <ScheduleButton
                label={labels.label}
                ariaLabel={labels.ariaLabel}
                large
                icon="Calendar-Outline"
                onClick={
                  hasNoMatchingTimeSlot
                    ? event => this.removeNoMatchingTimeSlot(event, program)
                    : event => this.initScheduling(event, program)
                }
                disabled={!eligible}
              />,
              true,
            )}
          </div>
        </Mobile>
      </div>
    );
  };

  renderForNormalView = labels => {
    const { program, intl, eligible } = this.props;
    const hasNoMatchingTimeSlot = program.schedule && program.schedule.no_matching_time_slot;

    if (program.schedule && program.schedule.deadline_passed) {
      return (
        <div>
          <FormattedMessage {...localMessages.scheduleDeadlinePassed} />
        </div>
      );
    }

    if (!labels.label) {
      return null;
    }

    return (
      <div className={style.scheduleInfoContainer}>
        <div className={style.scheduleInfo}>
          <div className={style.scheduleInfoAdmissionMethodContainer}>
            <div className={style.scheduleInfoTitle}>
              <FormattedMessage {...messages.generalStringAdmissionMethod} />
            </div>
            <div>{program.admission_method && program.admission_method.name}</div>
          </div>
          <div className={style.scheduleInfoDeadlineContainer}>
            <div className={style.scheduleInfoTitle}>
              <FormattedMessage {...messages.generalStringScheduleDeadline} />
            </div>
            <div>{program.schedule ? formatDate(program.schedule.deadline, intl) : '-'}</div>
          </div>
        </div>
        <div className={style.scheduleInfoScheduleButtonContainer}>
          <Default>
            {this.renderNonEligibleTooltip(
              <ButtonAction
                label={labels.label}
                ariaLabel={labels.ariaLabel}
                onClick={
                  hasNoMatchingTimeSlot
                    ? event => this.removeNoMatchingTimeSlot(event, program)
                    : event => this.initScheduling(event, program)
                }
                disabled={!eligible}
                icon="Calendar-Outline"
                large
              />,
            )}
          </Default>
          <Mobile>
            {this.renderNonEligibleTooltip(
              <Button
                label={labels.label}
                ariaLabel={labels.ariaLabel}
                iconLeft="Calendar-Outline"
                onClick={
                  hasNoMatchingTimeSlot
                    ? event => this.removeNoMatchingTimeSlot(event, program)
                    : event => this.initScheduling(event, program)
                }
                iconColor={colorTeal}
                disabled={!eligible}
              />,
              true,
            )}
          </Mobile>
        </div>
      </div>
    );
  };

  // TODO: Refactor the button code label/aria label code.
  /* getReScheduleLabel = () => {
    const { intl } = this.props;
    return {
      labelValue: customisedIntlFormatMessage(intl, messages.buttonReScheduleCancel),
      ariaLabel: this.getButtonAriaLabelValue(localMessages.buttonReScheduleCancelWithProgramSchoolDescription)
    }
  }

  getScheduleLabel = () => {
    const { intl } = this.props;
    return {
      labelValue: customisedIntlFormatMessage(intl, messages.buttonScheduleAppointment),
      ariaLabel: this.getButtonAriaLabelValue(localMessages.buttonScheduleAppointmentWithProgramSchoolDescription)
    }
  }

  getExpressInterestLabel = () => {}

  getNoTimeSlotLabel = () => {}

  getButtonAriaLabelValue = (ariaLabelValue) => {
    const { program, intl } = this.props;

    return (customisedIntlFormatMessage(intl, ariaLabelValue, {
          programTitle: program.name,
          schoolName: program.school.name,
        }));

  }

  */

  render() {
    const { program, intl, isSHS } = this.props;

    let isSHSLaGuardia = false;
    if (program.school.dbn === LA_GUARDIA_DBN && isSHS) {
      isSHSLaGuardia = true;
    }

    let labelValue;
    let ariaLabelValue;

    if (program.appointment) {
      labelValue = messages.buttonReScheduleCancel;
      ariaLabelValue = localMessages.buttonReScheduleCancelWithProgramSchoolDescription;
    } else if (program.schedule) {
      labelValue = messages.buttonScheduleAppointment;
      ariaLabelValue = localMessages.buttonScheduleAppointmentWithProgramSchoolDescription;
      if (program.schedule.expression_of_interest.id) {
        if (isSHSLaGuardia) {
          labelValue = messages.buttonUnExpressInterestLaGuardiaCase;
          ariaLabelValue = localMessages.buttonUnExpressInterestLaGuardiaWithProgramSchoolDescription;
        } else {
          labelValue = messages.buttonUnExpressInterest;
          ariaLabelValue = localMessages.buttonUnExpressInterestWithProgramSchoolDescription;
        }
      } else if (program.schedule.express_interest) {
        if (isSHSLaGuardia) {
          labelValue = messages.buttonExpressInterestLaGuardiaCase;
          ariaLabelValue = localMessages.buttonExpressInterestLaGuardiaWithProgramSchoolDescription;
        } else if (isMS) {
          labelValue = messages.buttonExpressInterestMSNonAudition;
          ariaLabelValue = localMessages.buttonExpressInterestLaGuardiaWithProgramSchoolDescription;
        } else {
          labelValue = messages.buttonExpressInterest;
          ariaLabelValue = localMessages.buttonExpressInterestWithProgramSchoolDescription;
        }
      } else if (program.schedule.no_matching_time_slot) {
        labelValue = messages.buttonCancelRegistration;
        ariaLabelValue = localMessages.buttonCancelRegistrationWithProgramSchoolDescription;
      }
    }
    const label = labelValue ? customisedIntlFormatMessage(intl, labelValue) : null;
    const ariaLabel = ariaLabelValue
      ? customisedIntlFormatMessage(intl, ariaLabelValue, {
          programTitle: program.name,
          schoolName: program.school.name,
        })
      : null;
    return this.props.isDetailView
      ? this.renderForDetailView({ label, ariaLabel })
      : this.renderForNormalView({ label, ariaLabel });
  }
}

ScheduleInfo.propTypes = {
  program: PropTypes.shape().isRequired,
  initScheduling: PropTypes.func.isRequired,
  removeNoMatchingTimeSlot: PropTypes.func.isRequired,
  isDetailView: PropTypes.bool,
  eligible: PropTypes.bool.isRequired,
  studentFirstName: PropTypes.string.isRequired,
  isRtl: PropTypes.bool,
  intl: PropTypes.shape().isRequired,
  isSHS: PropTypes.bool.isRequired,
};

ScheduleInfo.defaultProps = {
  isDetailView: false,
  isRtl: false,
};

export default withRtl(style)(withStyles(style)(injectIntl(ScheduleInfo)));
